import { useQuery } from '@tanstack/react-query';
import { Store, Sprout } from 'lucide-react';
import { request } from '../api/client';
import { formatIstTime } from '../lib/time';
import { RetailerLink, FarmerLink } from './EntityLinks';

/** One dealer / farmer registered on a workday — `GET /ffa/registrations`. */
export interface DayRegistration {
  kind: 'RETAILER' | 'FARMER';
  id: string;
  name: string;
  code: string | null;
  city: string | null;
  status: string | null;
  createdAt: string;
  sessionId: string | null;
  /** Which shift of the day it was added in; null = added while not on a shift. */
  shiftSeq: number | null;
}

/**
 * The dealers and farmers a person registered on one workday, each with its shift. `userId` omitted =
 * the caller (My Field Day). Hierarchy-scoped on the server, like the visit list.
 */
export function useDayRegistrations(date: string | null | undefined, userId?: string | number | null, enabled = true) {
  return useQuery({
    queryKey: ['day-registrations', userId ?? 'me', date],
    queryFn: () => request<DayRegistration[]>({ url: '/ffa/registrations', params: { date, ...(userId ? { userId } : {}) } }),
    enabled: enabled && !!date,
  });
}

/**
 * A failed load must not read as "nobody registered anyone" — say it did not load.
 */
export function RegistrationsLoadError({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <div className="rounded-lg border border-danger-500/30 bg-danger-500/5 px-3 py-2 type-caption text-danger-600 dark:text-danger-500">
      Couldn’t load the dealers and farmers registered that day — refresh to try again.
    </div>
  );
}

/** Those added in shift `no` (null = off a shift). */
export const registrationsIn = (regs: DayRegistration[] | undefined, no: number | null) =>
  (regs ?? []).filter((r) => (r.shiftSeq ?? null) === no);

/**
 * "New retailer · Shree Ganesh Krishi Kendra · R-000871 · Sangamner · 12:40 PM" — one line per customer
 * registered, each opening its record. Teal for a dealer, green for a farmer: the same two hues the
 * visit rows use for the party they went to.
 */
export function RegistrationList({ items, title }: { items: DayRegistration[]; title?: string }) {
  if (!items.length) return null;
  return (
    <div className="flex flex-col gap-1.5">
      {title && <div className="type-overline text-subtle">{title}</div>}
      {items.map((r) => {
        const retailer = r.kind === 'RETAILER';
        const Icon = retailer ? Store : Sprout;
        const tint = retailer ? 'bg-teal-500/10 text-teal-700 dark:text-teal-300' : 'bg-brand-500/10 text-brand-700 dark:text-brand-400';
        const Link = retailer ? RetailerLink : FarmerLink;
        return (
          <div key={`${r.kind}-${r.id}`} className="flex min-w-0 items-center gap-2 rounded-lg border border-line bg-surface-2 px-2.5 py-1.5">
            <span className={`grid size-7 shrink-0 place-items-center rounded-full ${tint}`}><Icon className="size-4" /></span>
            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 flex-wrap items-center gap-x-1.5">
                <span className={`rounded px-1.5 type-caption font-medium ${tint}`}>{retailer ? 'New retailer' : 'New farmer'}</span>
                <Link id={r.id}><span className="type-body-strong">{r.name}</span></Link>
                {r.code && <span className="font-mono text-xs text-subtle">{r.code}</span>}
              </div>
              {r.city && <div className="type-caption text-subtle">{r.city}</div>}
            </div>
            <span className="shrink-0 type-caption tabular-nums text-subtle">{formatIstTime(r.createdAt)}</span>
          </div>
        );
      })}
    </div>
  );
}
